import React, { useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  getEditorialBoardMembers,
  createEditorialBoardMember,
  updateEditorialBoardMember,
  deleteEditorialBoardMember,
  reorderEditorialBoardMembers,
} from '../api/client';
import { useAuth } from '../App';

const COUNTRY_OPTIONS = [
  { code: 'UZ', label: "🇺🇿 O'zbekiston" },
  { code: 'RU', label: '🇷🇺 Rossiya' },
  { code: 'KZ', label: "🇰🇿 Qozog'iston" },
  { code: 'KG', label: "🇰🇬 Qirg'iziston" },
  { code: 'TJ', label: '🇹🇯 Tojikiston' },
  { code: 'TR', label: '🇹🇷 Turkiya' },
  { code: 'CN', label: '🇨🇳 Xitoy' },
  { code: 'US', label: '🇺🇸 AQSH' },
  { code: 'GB', label: '🇬🇧 Britaniya' },
  { code: 'DE', label: '🇩🇪 Germaniya' },
  { code: 'ID', label: '🇮🇩 Indoneziya' },
  { code: 'JP', label: '🇯🇵 Yaponiya' },
  { code: 'KR', label: "🇰🇷 Janubiy Koreya" },
  { code: 'IN', label: '🇮🇳 Hindiston' },
];

const EMPTY_FORM = {
  full_name: '', full_name_en: '', full_name_ru: '',
  role: '', role_en: '', role_ru: '',
  section: '', section_en: '', section_ru: '',
  affiliation: '', affiliation_en: '', affiliation_ru: '',
  country: 'UZ', email: '',
  orcid: '', scopus_id: '', wos_researcher_id: '', google_scholar_id: '',
  h_index: null, photo_url: '', display_order: 0, is_active: true,
};

// Editorial-board section grouping. The two leadership groups are kept
// separately at the top so they always sort before the discipline sections.
const BOARD_SECTIONS = [
  // — Leadership groups (rendered first)
  { value: "Rahbariyat", label: "Rahbariyat (Bosh muharrir va mas'ul muharrirlar)", group: 'leadership' },
  { value: 'Maslahat kengashi', label: "Maslahat kengashi (Advisory Board)", group: 'leadership' },
  // — Discipline groups (match the article submission sections)
  { value: '01.00.00 - Fizika-matematika fanlari', label: '01.00.00 — Fizika-matematika fanlari' },
  { value: '02.00.00 - Kimyo fanlari', label: '02.00.00 — Kimyo fanlari' },
  { value: '03.00.00 - Biologiya fanlari', label: '03.00.00 — Biologiya fanlari' },
  { value: '05.00.00 - Texnika fanlari', label: '05.00.00 — Texnika fanlari' },
  { value: "06.00.00 - Qishloq xo'jaligi fanlari", label: "06.00.00 — Qishloq xo'jaligi fanlari" },
  { value: '07.00.00 - Tarix fanlari', label: '07.00.00 — Tarix fanlari' },
  { value: '08.00.00 - Iqtisod fanlari', label: '08.00.00 — Iqtisod fanlari' },
  { value: '09.00.00 - Falsafa fanlari', label: '09.00.00 — Falsafa fanlari' },
  { value: '10.00.00 - Filologiya fanlari', label: '10.00.00 — Filologiya fanlari' },
  { value: '11.00.00 - Geografiya fanlari', label: '11.00.00 — Geografiya fanlari' },
  { value: '13.00.00 - Pedagogika fanlari', label: '13.00.00 — Pedagogika fanlari' },
  { value: '19.00.00 - Psixologiya fanlari', label: '19.00.00 — Psixologiya fanlari' },
];

// Editorial roles ordered by hierarchy. The first entries are leadership
// positions that imply the "Rahbariyat" section; below them are member roles.
const BOARD_ROLES = [
  { value: 'Bosh muharrir', label: "Bosh muharrir (Editor-in-Chief)", implies_section: 'Rahbariyat' },
  { value: "Bosh muharrir o'rinbosari", label: "Bosh muharrir o'rinbosari (Deputy Editor)", implies_section: 'Rahbariyat' },
  { value: "Mas'ul muharrir", label: "Mas'ul muharrir (Managing Editor)", implies_section: 'Rahbariyat' },
  { value: 'Bo\'lim muharriri', label: "Bo'lim muharriri (Section Editor)" },
  { value: 'Texnik muharrir', label: "Texnik muharrir (Technical Editor)", implies_section: 'Rahbariyat' },
  { value: 'Statistik maslahatchi', label: 'Statistik maslahatchi (Statistical Advisor)', implies_section: 'Maslahat kengashi' },
  { value: "Maslahatchi a'zo", label: "Maslahatchi a'zo (Advisory Member)", implies_section: 'Maslahat kengashi' },
  { value: "Tahrir hay'ati a'zosi", label: "Tahrir hay'ati a'zosi (Editorial Board Member)" },
];

/* ── Reusable field row ── */
function Field({ label, value, onChange, type = 'text', placeholder, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{label}</label>
      <input
        type={type}
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
      />
    </div>
  );
}

/* ── Add/Edit modal ── */
function MemberModal({ member, onClose, onSave }) {
  const isEdit = !!member?.id;
  const [form, setForm] = useState(() => {
    if (member?.id) {
      const f = {};
      Object.keys(EMPTY_FORM).forEach(k => { f[k] = member[k] ?? EMPTY_FORM[k]; });
      return f;
    }
    return { ...EMPTY_FORM };
  });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('main');

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));
  const setNum = (key) => (e) => {
    const v = e.target.value;
    setForm(prev => ({ ...prev, [key]: v === '' ? null : Number(v) }));
  };

  const handleSave = async () => {
    if (!form.full_name.trim()) {
      toast.error("Ism (UZ) to'ldirilishi shart");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'main', label: 'Asosiy' },
    { id: 'translations', label: 'Tarjimalar' },
    { id: 'academic', label: 'Ilmiy IDlar' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 pt-[10vh] overflow-y-auto" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
            {isEdit ? "A'zoni tahrirlash" : "Yangi a'zo qo'shish"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-xl">&times;</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-700 px-6">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {tab === 'main' && (
            <>
              <Field label="F.I.Sh. (UZ) *" value={form.full_name} onChange={set('full_name')} placeholder="Misol: A. Karimov" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Lavozim *</label>
                  <select
                    value={form.role}
                    onChange={(e) => {
                      const val = e.target.value;
                      const found = BOARD_ROLES.find((r) => r.value === val);
                      setForm((prev) => ({
                        ...prev,
                        role: val,
                        // Auto-fill section when the role implies one (e.g. Bosh muharrir → Rahbariyat)
                        section: found?.implies_section || prev.section,
                      }));
                    }}
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="">— Tanlang —</option>
                    {BOARD_ROLES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Bo'lim / Yo'nalish *
                  </label>
                  <select
                    value={form.section}
                    onChange={set('section')}
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="">— Tanlang —</option>
                    <optgroup label="Rahbariyat">
                      {BOARD_SECTIONS.filter((s) => s.group === 'leadership').map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Ilmiy yo'nalishlar">
                      {BOARD_SECTIONS.filter((s) => !s.group).map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              </div>
              <Field label="Muassasa (UZ)" value={form.affiliation} onChange={set('affiliation')} placeholder="Farg'ona davlat universiteti" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Davlat</label>
                  <select
                    value={form.country}
                    onChange={set('country')}
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="">—</option>
                    {COUNTRY_OPTIONS.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                </div>
                <Field label="Email" value={form.email} onChange={set('email')} type="email" placeholder="email@example.com" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Tartib raqami" value={form.display_order} onChange={setNum('display_order')} type="number" />
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Faol</span>
                  </label>
                </div>
              </div>
            </>
          )}

          {tab === 'translations' && (
            <>
              <p className="text-xs text-slate-500 dark:text-slate-400 italic">Inglizcha va ruscha versiyalarni ko'rsating (Scopus uchun inglizcha majburiy)</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="F.I.Sh. (EN)" value={form.full_name_en} onChange={set('full_name_en')} placeholder="A. Karimov" />
                  <Field label="F.I.Sh. (RU)" value={form.full_name_ru} onChange={set('full_name_ru')} placeholder="А. Каримов" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Lavozim (EN)" value={form.role_en} onChange={set('role_en')} placeholder="Editor-in-Chief" />
                  <Field label="Lavozim (RU)" value={form.role_ru} onChange={set('role_ru')} placeholder="Главный редактор" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Bo'lim (EN)" value={form.section_en} onChange={set('section_en')} placeholder="Exact Sciences" />
                  <Field label="Bo'lim (RU)" value={form.section_ru} onChange={set('section_ru')} placeholder="Точные науки" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Muassasa (EN)" value={form.affiliation_en} onChange={set('affiliation_en')} placeholder="Fergana State University" />
                  <Field label="Muassasa (RU)" value={form.affiliation_ru} onChange={set('affiliation_ru')} placeholder="Ферганский государственный университет" />
                </div>
              </div>
            </>
          )}

          {tab === 'academic' && (
            <>
              <p className="text-xs text-slate-500 dark:text-slate-400 italic">Scopus indekslash uchun ilmiy identifikatorlarni kiriting</p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="ORCID" value={form.orcid} onChange={set('orcid')} placeholder="0000-0000-0000-0000" />
                <Field label="Scopus Author ID" value={form.scopus_id} onChange={set('scopus_id')} placeholder="12345678900" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="WOS Researcher ID" value={form.wos_researcher_id} onChange={set('wos_researcher_id')} placeholder="AAA-0000-0000" />
                <Field label="Google Scholar ID" value={form.google_scholar_id} onChange={set('google_scholar_id')} placeholder="XXXXXXXXXXXX" />
              </div>
              <Field label="h-index" value={form.h_index ?? ''} onChange={setNum('h_index')} type="number" className="w-32" />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
            Bekor qilish
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors"
          >
            {saving ? 'Saqlanmoqda...' : isEdit ? 'Saqlash' : "Qo'shish"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main page ── */
export default function EditorialBoardPage() {
  const { selectedJournalSlug } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalMember, setModalMember] = useState(null); // null = closed, {} = new, {id,...} = edit
  const [search, setSearch] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await getEditorialBoardMembers();
      setMembers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error("A'zolarni yuklashda xatolik");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchMembers();
  }, [fetchMembers, selectedJournalSlug]);

  /* Sections list */
  const sections = useMemo(() => {
    const s = new Set();
    members.forEach(m => { if (m.section) s.add(m.section); });
    return ['', ...Array.from(s).sort()];
  }, [members]);

  /* Filtered + sorted */
  const filtered = useMemo(() => {
    let list = [...members];
    if (!showInactive) list = list.filter(m => m.is_active);
    if (sectionFilter) list = list.filter(m => m.section === sectionFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        (m.full_name || '').toLowerCase().includes(q) ||
        (m.full_name_en || '').toLowerCase().includes(q) ||
        (m.role || '').toLowerCase().includes(q) ||
        (m.role_en || '').toLowerCase().includes(q) ||
        (m.affiliation || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q) ||
        (m.orcid || '').toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
    return list;
  }, [members, showInactive, sectionFilter, search]);

  /* Save handler */
  const handleSave = async (form) => {
    try {
      if (modalMember?.id) {
        const res = await updateEditorialBoardMember(modalMember.id, form);
        setMembers(prev => prev.map(m => m.id === modalMember.id ? res.data : m));
        toast.success("A'zo yangilandi");
      } else {
        const res = await createEditorialBoardMember(form);
        setMembers(prev => [...prev, res.data]);
        toast.success("Yangi a'zo qo'shildi");
      }
      setModalMember(null);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Xatolik yuz berdi';
      toast.error(msg);
      throw err;
    }
  };

  /* Delete handler */
  const handleDelete = async (id) => {
    try {
      await deleteEditorialBoardMember(id);
      setMembers(prev => prev.filter(m => m.id !== id));
      toast.success("A'zo o'chirildi");
    } catch (err) {
      toast.error("O'chirishda xatolik");
    }
  };

  /* Toggle active */
  const handleToggleActive = async (member) => {
    try {
      const res = await updateEditorialBoardMember(member.id, { is_active: !member.is_active });
      setMembers(prev => prev.map(m => m.id === member.id ? res.data : m));
      toast.success(res.data.is_active ? 'Faollashtirildi' : "O'chirildi");
    } catch {
      toast.error('Xatolik');
    }
  };

  /* Move up/down */
  const handleMove = async (member, direction) => {
    const sorted = [...members].sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
    const idx = sorted.findIndex(m => m.id === member.id);
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= sorted.length) return;

    // Swap
    const ids = sorted.map(m => m.id);
    [ids[idx], ids[target]] = [ids[target], ids[idx]];
    try {
      await reorderEditorialBoardMembers(ids);
      await fetchMembers();
    } catch {
      toast.error('Tartib almashtirishda xatolik');
    }
  };

  const countryFlag = (code) => {
    const c = COUNTRY_OPTIONS.find(o => o.code === code);
    return c ? c.label : code || '—';
  };

  const academicBadge = (value, label, color) => {
    if (!value) return null;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Tahririyat hay'ati</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Jami: {members.length} a'zo · Faol: {members.filter(m => m.is_active).length}
          </p>
        </div>
        <button
          onClick={() => setModalMember({})}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Yangi a'zo
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ism, lavozim, tashkilot bo'yicha qidiring..."
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
        <select
          value={sectionFilter}
          onChange={(e) => setSectionFilter(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
        >
          <option value="">Barcha bo'limlar</option>
          {sections.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          Nofaollarni ko'rsat
        </label>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-700/50 text-left">
                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300 w-10">#</th>
                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">F.I.Sh.</th>
                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Lavozim</th>
                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Bo'lim</th>
                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Muassasa</th>
                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Davlat</th>
                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">IDlar</th>
                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300 w-28">Amallar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                    {search || sectionFilter ? 'Hech narsa topilmadi' : "Hali a'zolar qo'shilmagan"}
                  </td>
                </tr>
              ) : filtered.map((m, i) => (
                <tr key={m.id} className={`hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors ${!m.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3 text-slate-400 font-mono text-xs">{m.display_order}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800 dark:text-white">{m.full_name}</div>
                    {m.full_name_en && <div className="text-xs text-slate-400">{m.full_name_en}</div>}
                    {m.email && <div className="text-xs text-blue-500">{m.email}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    <div>{m.role || '—'}</div>
                    {m.role_en && <div className="text-xs text-slate-400">{m.role_en}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300 max-w-[200px]">
                    <div className="truncate">{m.section || '—'}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300 max-w-[200px]">
                    <div className="truncate">{m.affiliation || '—'}</div>
                  </td>
                  <td className="px-4 py-3">{countryFlag(m.country)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {academicBadge(m.orcid, 'ORCID', 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400')}
                      {academicBadge(m.scopus_id, 'Scopus', 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400')}
                      {academicBadge(m.wos_researcher_id, 'WOS', 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400')}
                      {academicBadge(m.google_scholar_id, 'GS', 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400')}
                      {m.h_index != null && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-600 dark:text-slate-200">
                          h={m.h_index}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleMove(m, 'up')}
                        title="Yuqoriga"
                        className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                      </button>
                      <button
                        onClick={() => handleMove(m, 'down')}
                        title="Pastga"
                        className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      <button
                        onClick={() => setModalMember(m)}
                        title="Tahrirlash"
                        className="p-1.5 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                      <button
                        onClick={() => handleToggleActive(m)}
                        title={m.is_active ? "O'chirish" : "Faollashtirish"}
                        className={`p-1.5 rounded-md transition-colors ${m.is_active ? 'text-slate-400 hover:text-amber-600 hover:bg-slate-100 dark:hover:bg-slate-700' : 'text-amber-500 hover:text-green-600 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                      >
                        {m.is_active ? (
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.758 6.758M9.878 9.878l-3.12-3.12m7.362 7.362l3.12 3.12M3 3l18 18" /></svg>
                        ) : (
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`"${m.full_name}"ni butunlay o'chirmoqchimisiz?`)) handleDelete(m.id);
                        }}
                        title="O'chirish"
                        className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {modalMember !== null && (
        <MemberModal
          member={modalMember}
          onClose={() => setModalMember(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
